import type { ReactNode } from "react";
import { Flex } from "@/components/Flex/Flex";
import { Stack } from "@/components/Stack/Stack";
import { Text } from "@/components/Text";
import { MailIcon } from "./icons";

/** "We emailed you the details" plus the spam-folder hint and the support
 *  mailto. Two presentations of the same content:
 *
 *  - `compact` — an icon and two lines. Used when an `event` card is present:
 *    the эфир date is what the visitor came back to check, and the boxed version
 *    pushed the connect CTA down on a 360×780 screen.
 *  - the boxed panel — the default, and the only one that can carry the numbered
 *    `steps` of `mode: "confirm"`.
 *
 *  The `mailto` is in BOTH. It is the page's only support affordance and the
 *  sole source of the `lm_next.link.support` event. */
export function DeliveryNote({
  compact,
  subtitle,
  spamTitle,
  spamNote,
  steps,
  supportEmail,
}: {
  compact: boolean;
  subtitle: ReactNode;
  spamTitle: string;
  spamNote: string;
  steps: string[];
  supportEmail: string;
}) {
  const support = (
    <a
      href={`mailto:${supportEmail}`}
      data-track="lm_next.link.support"
      className="ds-link ds-link-primary font-(weight:--fw-medium)"
    >
      {supportEmail}
    </a>
  );

  if (compact) {
    return (
      <Flex
        data-component="DeliveryNote"
        align="start"
        gap={12}
        className="mx-auto mt-6 max-w-xl text-left"
      >
        <span className="text-muted">
          <MailIcon />
        </span>
        <div className="min-w-0 flex-1">
          <Text variant="body-sm" as="p" className="text-fg-soft">
            {subtitle}
          </Text>
          <Text variant="body-sm" as="p" className="mt-1 text-muted">
            {spamNote} Не нашли письмо? {support}
          </Text>
        </div>
      </Flex>
    );
  }

  return (
    <div
      data-component="DeliveryNote"
      className="mx-auto mt-6 max-w-xl rounded-2xl border border-border bg-bg p-4 text-left md:mt-8 md:p-6"
    >
      <Text variant="label" as="p" className="flex items-center gap-2 text-fg">
        <span aria-hidden="true">📩</span>
        {spamTitle}
      </Text>
      {steps.length > 0 && (
        <Stack as="ol" gap={8} className="mt-3">
          {steps.map((step, index) => (
            <Flex as="li" key={step} align="start" gap={8}>
              <Flex
                as="span"
                align="center"
                justify="center"
                className="mt-px h-5 w-5 shrink-0 rounded-full bg-brand text-(length:--type-caption) font-(weight:--fw-bold) text-brand-content"
              >
                {index + 1}
              </Flex>
              <Text variant="body-sm" as="span" className="flex-1 text-muted">
                {step}
              </Text>
            </Flex>
          ))}
        </Stack>
      )}
      <Text variant="body-sm" as="p" className="mt-3 text-muted">
        {spamNote} Не нашли письмо? {support}
      </Text>
    </div>
  );
}

DeliveryNote.displayName = "DeliveryNote";
